import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import PageHeading from '../../components/PageHeading';
import SectionCard, { sectionTableHeadClass } from '../../components/SectionCard';

function when(value) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
}

function AppShelf() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['app-shelf'],
    queryFn: async () => (await api.get('/app-shelf')).data,
  });

  const versions = data?.versions || [];
  const released = data?.data || null;
  const waiting = versions.filter((row) => !row.on_shelf);
  const installs = data?.installs || [];
  const canPlace = Boolean(data?.can_place);

  const qr = useQuery({
    queryKey: ['app-shelf-qr', released?.id],
    enabled: released?.id != null,
    queryFn: async () => (await api.get(`/app-shelf/${released.id}/qr`)).data?.data,
  });

  const release = useMutation({
    mutationFn: async (id) => (await api.post(`/app-shelf/${id}/release`)).data,
    onSuccess: async (body) => {
      await queryClient.invalidateQueries({ queryKey: ['app-shelf'] });
      const name = body?.data?.version_name || 'That version';
      toast.success(`${name} is the version phones receive.`);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.error || 'Could not put that version on the shelf');
    },
  });

  return (
    <div>
      <PageHeading
        title="App shelf"
        subPages={[
          { id: 'staff', label: 'Staff', to: '/people' },
          { id: 'shelf', label: 'App shelf', to: '/people/app-shelf', active: true },
        ]}
      />
      <SectionCard title="Versions">
        {isLoading ? <p className="text-sm text-gray-700">Loading...</p> : null}
        {!isLoading && !versions.length ? (
          <p className="text-sm text-gray-700">No app version is on this page yet.</p>
        ) : null}
        {released ? (
          <p className="text-sm text-gray-700">
            {released.version_name} is the version phones receive. When a worker opens the app, the update sign appears. They tap OK, then Open.
          </p>
        ) : null}
        {waiting.length ? (
          <p className="text-sm text-gray-700">
            {waiting.map((row) => row.version_name).join(', ')} {waiting.length === 1 ? 'is' : 'are'} on this page. Put on the shelf when phones should take that update.
          </p>
        ) : null}
        {versions.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-gray-800">
              <thead className={sectionTableHeadClass}>
                <tr>
                  <th className="py-2 pr-3">Version</th>
                  <th className="py-2 pr-3">Code</th>
                  <th className="py-2 pr-3">Placed</th>
                  <th className="py-2">Shelf</th>
                </tr>
              </thead>
              <tbody>
                {versions.map((row) => (
                  <tr key={row.id}>
                    <td className="py-2 pr-3">{row.version_name}</td>
                    <td className="py-2 pr-3">{row.version_code}</td>
                    <td className="py-2 pr-3">{when(row.created_at)}</td>
                    <td className="py-2">
                      {row.on_shelf ? (
                        <span className="font-semibold text-gray-900">On the shelf</span>
                      ) : canPlace ? (
                        <button
                          type="button"
                          disabled={release.isPending}
                          onClick={() => release.mutate(row.id)}
                          className="px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50"
                        >
                          Put on the shelf
                        </button>
                      ) : (
                        <span>Waiting</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </SectionCard>

      {released ? (
        <SectionCard title={released.version_name}>
          <div className="flex flex-wrap items-start gap-6 text-sm text-gray-800">
            {qr.data?.qr_data_url ? (
              <img src={qr.data.qr_data_url} alt="QR code that installs this app version" className="h-56 w-56 bg-white p-2" />
            ) : (
              <p>{qr.isLoading ? 'Preparing the code…' : 'The code could not be made.'}</p>
            )}
            <div className="space-y-1">
              <p>Version {released.version_name} · on the shelf</p>
              <p>A new phone scans this code. A phone that already has the app updates from the sign inside the app.</p>
              {released.notes ? <p>{released.notes}</p> : null}
            </div>
          </div>
        </SectionCard>
      ) : null}

      <SectionCard title="Install record">
        {!installs.length ? <p className="text-sm text-gray-700">No phone has taken an app from the shelf yet.</p> : null}
        {installs.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-gray-800">
              <thead className={sectionTableHeadClass}>
                <tr>
                  <th className="py-2 pr-3">When</th>
                  <th className="py-2 pr-3">Version</th>
                  <th className="py-2 pr-3">What</th>
                  <th className="py-2">Who</th>
                </tr>
              </thead>
              <tbody>
                {installs.map((row) => (
                  <tr key={row.id}>
                    <td className="py-2 pr-3">{when(row.created_at)}</td>
                    <td className="py-2 pr-3">{row.version_name || row.version_code}</td>
                    <td className="py-2 pr-3">{row.kind === 'running' ? 'Opened the app' : 'Downloaded'}</td>
                    <td className="py-2">{row.username || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </SectionCard>
    </div>
  );
}

export default AppShelf;
